/**
 * STFFLG-4184 Slice 5 (contract S5.1): a job order invitation email's link names the branch it
 * opens in, `?agencyId=<id>` (mspplatform invitationEmailActionUrl): the invitee's branch on the
 * invitee's View Invitation / View Pending Orders link, the host branch on a host-side Open Order
 * link. Pending Orders lists only the selected branch's invitations, and the order page reads the
 * selected branch's distribution, so a user working in another of their branches would land on a
 * page without the invitation, or on an order their selected branch cannot open.
 *
 * The in-app notification does the same from its own `agencyId` (invitationBranchToSelect). These
 * are the pure rules for the link; BranchFromLinkGate applies them.
 *
 * The parameter is never trusted: it only ever selects a branch the signed-in user already HOLDS
 * (their home branch or one of their `agencyAssignments`), exactly as the header's switcher could.
 */

/** The query parameter the invitation emails add (mspplatform INVITATION_LINK_AGENCY_PARAM). */
export const BRANCH_LINK_PARAM = 'agencyId'

export interface BranchFromLinkUser {
  /** The branch selected in the header (`loggedInAgencyId`); '' or absent = none yet. */
  loggedInAgencyId?: string | null
  /** The user's home branch (`currentUser.agencyId`). */
  homeAgencyId?: string | null
  /** The user's `agencyAssignments` ids. */
  agencyAssignmentIds?: ReadonlyArray<string | null | undefined>
  /** The selected role's category (`backendRolePermissions.category`). */
  roleCategory?: string | null
  /** `backendRolePermissions.isPlatform`. */
  isPlatform?: boolean
}

const sameAgencyId = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * The branch to select before the linked page opens, or null for none:
 * - null for no (or a blank) parameter;
 * - null for an MSP or Platform user: they work every branch's orders from the MSP and never
 *   switch for a link (MSP users are unaffected);
 * - null when it is already the selected branch (with none selected yet, the home branch counts as
 *   selected: the API then reads the token's);
 * - null for a branch the user does not hold (ignored: never selected, no error);
 * - else the held id, in the user's own spelling (ids compare case-insensitively).
 */
export function branchToSelectFromLink(
  linkedAgencyId: string | null | undefined,
  user: BranchFromLinkUser,
): string | null {
  const target = linkedAgencyId?.trim()
  if (!target) return null
  if (user.isPlatform || user.roleCategory === 'MSP' || user.roleCategory === 'Platform') {
    return null
  }

  const home = user.homeAgencyId?.trim() ?? ''
  const selected = user.loggedInAgencyId?.trim() || home
  if (selected && sameAgencyId(selected, target)) return null

  const held = [home, ...(user.agencyAssignmentIds ?? []).map((id) => id?.trim() ?? '')].filter(
    (id) => id !== '',
  )
  return held.find((id) => sameAgencyId(id, target)) ?? null
}

/** `search` without the branch parameter ('' when nothing else is left), other parameters kept. */
export function searchWithoutBranchParam(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(BRANCH_LINK_PARAM)
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}
